import React, { useState, useEffect } from 'react'
import { Input, Dropdown, Label, Form } from 'semantic-ui-react'
import { useSelector } from 'react-redux'
import {
  COMMON_CURRENCIES,
  selectCurrency
} from '../../redux/slices/currencySlice'

/**
 * Price input component with currency selection using Semantic UI
 */
const PriceInputUI = ({
  label,
  value = '',
  onChange,
  onCurrencyChange,
  currency,
  error,
  required = false,
  disabled = false,
  compact = false,
  placeholder = '0',
  disableCurrencyChange = false,
  ...props
}) => {
  const defaultCurrency = useSelector(selectCurrency)
  const [localValue, setLocalValue] = useState('')
  const [touched, setTouched] = useState(false)

  // Initialize from value if provided
  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value === 0 ? '' : String(value))
    }
  }, [value])

  // Convert COMMON_CURRENCIES to Semantic UI dropdown options format
  const currencyOptions = COMMON_CURRENCIES.map(curr => ({
    key: curr.code.toLowerCase(),
    text: `${curr.symbol} ${curr.code}`,
    value: curr.code,
    content: (
      <span>
        <span style={{ marginRight: '0.5rem' }}>{curr.symbol}</span>
        {curr.code}
      </span>
    )
  }))

  // Popular currencies at the top
  const popularCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CNY']
  currencyOptions.sort((a, b) => {
    const aIndex = popularCurrencies.indexOf(a.value)
    const bIndex = popularCurrencies.indexOf(b.value)

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1

    return a.text.localeCompare(b.text)
  })

  const handlePriceChange = (e, { value: newValue }) => {
    // Only allow numbers and a single decimal point
    if (newValue === '' || /^[0-9]*\.?[0-9]*$/.test(newValue)) {
      setLocalValue(newValue)

      if (!touched) setTouched(true)

      // Call the parent's onChange with either a number or empty string
      const numValue = newValue === '' ? '' : Number(newValue)
      onChange({
        target: {
          name: props.name || 'price',
          value: numValue
        }
      })
    }
  }

  const handleCurrencyChange = (e, { value: currencyCode }) => {
    if (onCurrencyChange) {
      onCurrencyChange(currencyCode)
    }
  }

  // Get selected currency data
  const selectedCurrency =
    COMMON_CURRENCIES.find(
      c => c.code === (currency || defaultCurrency.code)
    ) || COMMON_CURRENCIES.find(c => c.code === 'USD')

  return (
    <Form.Field required={required} error={!!error}>
      {/* {label && <label>{label}</label>} */}

      <Input
        placeholder={placeholder}
        value={localValue}
        onChange={handlePriceChange}
        disabled={disabled}
        error={!!error}
        labelPosition='right'
        size={compact ? 'small' : 'medium'}
        action={
          disableCurrencyChange ? (
            // When currency change is disabled, show a label indicating currency is fixed by country
            <Label 
              basic 
              color='teal'
              className='cursor-not-allowed'
              title='Currency is determined by your country and cannot be changed'
            >
              <span className="flex items-center">
                {selectedCurrency.symbol} {selectedCurrency.code}
              </span>
            </Label>
          ) : (
            // Normal dropdown when currency change is enabled
            <Dropdown
              button
              basic
              floating
              // className='icon'
              options={currencyOptions}
              value={currency || defaultCurrency.code}
              onChange={handleCurrencyChange}
              disabled={disabled}
              search
              compact={compact}
              text={selectedCurrency.code}
            />
          )
        }
        {...props}
      />

      {/* {error && (
        <div className="mt-1 text-sm text-red-600 whitespace-pre-line">
          {error}
        </div>
      )} */}
    </Form.Field>
  )
}

export default PriceInputUI
